import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "./useWorkspace";
import { useSuperAdmin } from "./useSuperAdmin";

export type AppRole = "admin" | "manager" | "analyst" | "operator" | "user";

export type AppModule =
  | "dashboard"
  | "users"
  | "users_roles"
  | "crm_clients"
  | "crm_contacts"
  | "crm_opportunities"
  | "crm_appointments"
  | "crm_tasks"
  | "crm_campaigns"
  | "erp_products"
  | "erp_inventory"
  | "erp_orders"
  | "erp_ewheel"
  | "finance_invoices"
  | "finance_payments"
  | "finance_pos"
  | "reports"
  | "documents"
  | "settings"
  | "workshop_orders"
  | "workshop_vehicles"
  | "workshop_invoices"
  | "workshop_rma"
  | "hrm_employees"
  | "hrm_recruitment"
  | "hrm_onboarding"
  | "hrm_payroll"
  | "hrm_training"
  | "chatbot_conversations"
  | "chatbot_knowledge"
  | "chatbot_promotions"
  | "chatbot_settings"
  | "vehicle_rental"
  | "google_business"
  | "finance_expenses"
  | "finance_reconciliation"
  | "seo_cro_ux"
  | "insurance_clients";

export type PermissionAction = "view" | "create" | "edit" | "delete";

interface ModulePermission {
  module: AppModule;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface UsePermissionsReturn {
  roles: AppRole[];
  permissions: ModulePermission[];
  loading: boolean;
  isAdmin: boolean;
  hasRole: (role: AppRole) => boolean;
  canAccess: (module: AppModule, action?: PermissionAction) => boolean;
  refreshPermissions: () => Promise<void>;
  workspaceId: string | null;
  hasActiveSubscription: boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();
  const { workspace, hasActiveSubscription, canAccessModule, loading: workspaceLoading, isOwner } = useWorkspace();
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [permissions, setPermissions] = useState<ModulePermission[]>([]);
  
  const userId = user?.id;
  const [loading, setLoading] = useState(true);

  const fetchRolesAndPermissions = useCallback(async () => {
    if (!userId) {
      setRoles([]);
      setPermissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch user roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (rolesError) {
        logger.error("Error fetching roles:", rolesError);
        return;
      }

      const userRoles = (rolesData?.map((r) => r.role) || ["user"]) as AppRole[];
      setRoles(userRoles);

      // Fetch permissions for user's roles
      const { data: permissionsData, error: permissionsError } = await supabase
        .from("module_permissions")
        .select("module, can_view, can_create, can_edit, can_delete")
        .in("role", userRoles);

      if (permissionsError) {
        logger.error("Error fetching permissions:", permissionsError);
        return;
      }

      // Merge permissions (OR logic - if any role has permission, grant it)
      const mergedPermissions: Record<string, ModulePermission> = {};

      permissionsData?.forEach((p) => {
        const module = p.module as AppModule;
        if (!mergedPermissions[module]) {
          mergedPermissions[module] = {
            module,
            can_view: false,
            can_create: false,
            can_edit: false,
            can_delete: false,
          };
        }
        mergedPermissions[module].can_view = mergedPermissions[module].can_view || p.can_view;
        mergedPermissions[module].can_create = mergedPermissions[module].can_create || p.can_create;
        mergedPermissions[module].can_edit = mergedPermissions[module].can_edit || p.can_edit;
        mergedPermissions[module].can_delete = mergedPermissions[module].can_delete || p.can_delete;
      });

      setPermissions(Object.values(mergedPermissions));
    } catch (error) {
      logger.error("Error in fetchRolesAndPermissions:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchRolesAndPermissions();
  }, [fetchRolesAndPermissions]);

  const isAdmin = roles.includes("admin");

  const hasRole = useCallback(
    (role: AppRole) => roles.includes(role),
    [roles]
  );

  const canAccess = useCallback(
    (module: AppModule, action: PermissionAction = "view") => {
      // Super admin has full access to everything
      if (isSuperAdmin) return true;
      
      // Admins have full access
      if (isAdmin) return true;

      // Workspace owners with active subscription can access any module in their plan
      if (isOwner && hasActiveSubscription) {
        // Dashboard and settings are always accessible
        if (module === "dashboard" || module === "settings") return true;
        // Check if module is included in the subscription plan
        return canAccessModule(module);
      }

      // Check if user has role-based permission first
      const permission = permissions.find((p) => p.module === module);
      if (!permission) return false;

      let hasRolePermission = false;
      switch (action) {
        case "view":
          hasRolePermission = permission.can_view;
          break;
        case "create":
          hasRolePermission = permission.can_create;
          break;
        case "edit":
          hasRolePermission = permission.can_edit;
          break;
        case "delete":
          hasRolePermission = permission.can_delete;
          break;
        default:
          hasRolePermission = false;
      }

      if (!hasRolePermission) return false;

      // Then check subscription access (only for non-view actions or sensitive modules)
      if (action !== "view") {
        if (!canAccessModule(module)) {
          return false;
        }
      }

      return true;
    },
    [permissions, isAdmin, isSuperAdmin, isOwner, hasActiveSubscription, canAccessModule]
  );

  // Super admin never shows loading state
  const effectiveLoading = superAdminLoading || (isSuperAdmin ? false : (loading || workspaceLoading));

  return {
    roles,
    permissions,
    loading: effectiveLoading,
    isAdmin: isAdmin || isSuperAdmin,
    hasRole,
    canAccess,
    refreshPermissions: fetchRolesAndPermissions,
    workspaceId: workspace?.id || null,
    hasActiveSubscription,
  };
}
